import i18n from '../../i18n';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, ApiAuthResponse, AuthenticationType } from '../../interfaces';
import {
  handleAibLoginCallback,
  cleanUpLocalStorage,
  loginToAccount,
  createAuthResponse,
  loginInternetCustomer,
} from '../../api/auth';
import { goToRedirectPath } from '../../utils/appUtils';
import './style.scss';
import LocationSelectModal from './location-select-modal';
import DeviceSwitchModal from './device-switch-modal';
import Spinner from '../../components/Spinner';
import Footer from '../../components/Footer';
import { isEmpty } from 'lodash';
import { handleLoginResponse as handleLoginResponseDispatch } from '../../views/app/actions';
import { useCallback, useEffect, useState } from 'react';

interface Props {
  location: string;
}

const Callback = ({ location }: Props) => {
  const dispatch = useDispatch();
  const loginError = useSelector((state: RootState) => state.authReducer.loginError as string);
  useSelector((state: RootState) => state.app.alertVisible);

  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);

  const handleLoginResponse = useCallback(
    (result?: ApiAuthResponse) => {
      handleLoginResponseDispatch(dispatch, result ? result : ({} as ApiAuthResponse));
    },
    [dispatch],
  );

  const showFailedMessageAndGoBack = (messageString?: string) => {
    setTimeout(() => goToRedirectPath(), 10000);
    setMessage(messageString ? messageString : i18n.t<string>('authentication failed'));
    setLoading(false);
  };

  const handleLogin = useCallback(async () => {
    if (location) {
      localStorage.removeItem(AuthenticationType.ACCESS_TOKEN);

      try {
        const accounts = await handleAibLoginCallback(location);

        if (isEmpty(accounts)) {
          cleanUpLocalStorage();
          showFailedMessageAndGoBack();

          return;
        }

        const accountsWithTvSubscribers = accounts.filter((account) => account.subscribers.length);

        if (isEmpty(accountsWithTvSubscribers)) {
          // Just pick the first account, as none of them have TV subscribers so it won't matter
          const loginResponse = await loginInternetCustomer(accounts[0]);
          handleLoginResponse(loginResponse);
          return;
        }

        const singleAccountWithSingleSubscriber =
          accountsWithTvSubscribers.length === 1 && accountsWithTvSubscribers[0].subscribers.length === 1;

        if (singleAccountWithSingleSubscriber) {
          const loginResponse = await loginToAccount(
            accountsWithTvSubscribers[0],
            accountsWithTvSubscribers[0].subscribers[0],
          );
          handleLoginResponse(loginResponse);
        } else {
          handleLoginResponse(createAuthResponse({ success: true, accounts: accountsWithTvSubscribers }));
        }
      } catch (errorFromHandleLogin) {
        if (typeof errorFromHandleLogin === 'string') {
          handleLoginResponse({ retcode: errorFromHandleLogin.replace('Error: ', '') } as ApiAuthResponse);
          return;
        }
        handleLoginResponse(errorFromHandleLogin as ApiAuthResponse);
      }
    }
  }, [handleLoginResponse, location]);

  useEffect(() => {
    const doLogin = async () => {
      await handleLogin();
    };

    doLogin();
  }, [handleLogin]);

  if (loginError) {
    setTimeout(() => {
      if (localStorage.getItem('call-me-back')) {
        // one extra index for when using call-me-back, because then the navigation stack has one more item
        window.history.go(-3);
      } else {
        window.history.go(-2);
      }
    }, 5000);

    return (
      <main className="loginLanding">
        <h3>{i18n.t<string>('checking authentication')}</h3>
        <p dangerouslySetInnerHTML={{ __html: message }} />
        <p dangerouslySetInnerHTML={{ __html: loginError }} />
        <Spinner wrapInContainer={true} />
        <p>{i18n.t<string>('redirecting')}</p>
        <Footer />
      </main>
    );
  }

  return (
    <main className="loginLanding">
      <DeviceSwitchModal />
      <LocationSelectModal />
      <h3>{i18n.t<string>('checking authentication')}</h3>
      <p dangerouslySetInnerHTML={{ __html: message }} />
      <p dangerouslySetInnerHTML={{ __html: loginError }} />
      {!loginError && loading && <Spinner wrapInContainer={true} />}
      <Footer />
    </main>
  );
};

export default Callback;
